<template>
    <div class="block desktop:hidden print:hidden relative z-40">
        <!-- menu bar! -->
        <div>
            <!-- <AppLogo :logo-link="logoLink" :target="target" /> -->
            <div class="relative bg-white flex justify-between items-center h-16 px-4 py-3 z-40 shadow">
                <div class="w-1/2 tablet:w-1/3 tabletPlus:w-1/4 mobilePlus:w-1/4">
                    <AppLogo
                        :logo-link="logoLink"
                        :target="target" />
                </div>

                <div class="flex gap-x-5">
                    <a
                        v-if="phoneNumber"
                        :href="`tel:${[isCarrierPage == true ? carrierPageNumber : phoneNumber]}`"
                        :data-category="`${{'residential':'resi:','business':'biz','communities':'com',}[currentSiteSection] || 'resi'}: internal page click`"
                        :data-action="`${{'residential':'resi:','business':'biz','communities':'com',}[currentSiteSection] || 'resi'}: call phone number`"
                        :data-label="`${{'residential':'resi:','business':'biz','communities':'com',}[currentSiteSection] || 'resi'}: mobile nav phone icon`"
                        class="track hover:text-blue-light"
                        aria-label="Phone"
                    >
                        <IconPhone class="text-xl" />
                    </a>
                    <button
                        v-if="includeSearch"
                        @click.prevent="toggleSearch()"
                        class="hover:text-blue-light"
                        aria-label="Search"
                    >
                        <IconSearch
                            class="text-xl"
                        />
                    </button>
                    <button
                        @click.prevent="showSlider()"
                        class="hover:text-blue-light"
                        aria-label="Bars"
                    >
                        <IconBars class="text-xl" />
                    </button>
                </div>
            </div>
             <transition name="slide-down">
                <div
                    v-show="d"
                    class="absolute z-30 w-full bg-gray-100 shadow p-4 overflow-scroll"
                >
                    <form method="GET" action="/search" class="flex flex-row gap-x-3">
                        <button
                            type="reset"
                            @click="toggleSearch()"
                            class="h-10 hover:text-blue-light"
                            aria-label="Close"
                        >
                            <IconXmark class="text-2xl" />
                        </button>
                        <div class="relative w-full">
                            <input
                                v-model="searchTerms"
                                class="border-1 border-gray-300 rounded-full w-full h-10"
                                name="search"
                                placeholder="Search.."
                                type="text"
                            />
                            <button type="submit" class="absolute top-0 right-0 flex items-center justify-center bg-navy-dark text-white w-10 h-10 rounded-full text-center hover:bg-blue-light" aria-label="Search">
                                <IconSearch />
                            </button>
                        </div>
                    </form>
                </div>
            </transition>
        </div>

        <!-- tint/overlay -->
        <transition name="fade">
            <div
                v-show="d || sliderIsVisible"
                class="fixed z-20 w-full h-full inset-0 bg-black opacity-75"
            />
        </transition>

        <!-- main slider -->
        <transition name="slide-right">
            <div
                v-show="sliderIsVisible"
                class="fixed z-50 inset-0 bg-white overflow-x-hidden overflow-y-auto"
            >
                <!-- immutable top part, same dimensions as menu bar -->
                <div class="relative flex justify-between items-center px-4 py-3 z-40 border-b border-gray-300">
                    <div class="font-bold text-sm">
                        {{ siteSection }}
                    </div>

                    <button @click.prevent="hideSlider()" aria-label="Close">
                        <IconXmark class="text-lg" />
                    </button>
                </div>

                <!-- main menu -->
                <transition name="slide-in-place">
                    <div
                        v-if="!selectedMenu && !selectedSubmenu"
                        class="px-4 divide-y-2 divide-gray-300"
                    >
                        <!-- primary items (from menu) -->
                        <div class="divide-y divide-gray-200 divide-solid font-bold">
                            <a
                                v-for="(menu, menuIndex) in menu"
                                v-if="canShow(menu.audience)"
                                :key="menuIndex"
                                :href="menu.url"
                                @click.stop="selectMenu($event, menu)"
                                class="py-4 flex justify-between items-center hover:text-blue-light"
                            >
                                <div class="flex items-center">
                                    <img
                                        :src="`/assets/icons/${menu.text.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').replace(/(_|-)+/g, '-')}.png`"
                                        alt=""
                                        class="w-8 mr-4">
                                    <div>
                                        {{ menu.text }}
                                    </div>
                                </div>
                                <IconChevronRight
                                    v-if="menu.items"
                                    class="ml-auto"
                                />
                            </a>
                        </div>

                        <!-- secondary items (userTools) -->
                        <div class="divide-y divide-gray-200 divide-solid">
                            <button
                                @click.prevent="$emit('change-location', hideSlider())"
                                class="block py-3 hover:text-blue-light"
                            >
                                Check Availability?
                            </button>
                            <a
                                v-for="(menu, menuIndex) in userTools"
                                v-if="canShow(menu.audience)"
                                :key="menuIndex"
                                :href="menu.url"
                                @click.stop="selectMenu($event, menu)"
                                class="py-3 flex items-center hover:text-blue-light"
                            >
                                <span v-html="menu.text" />
                                <span
                                    v-if="menu.items"
                                    class="ml-auto"
                                >
                                    <IconChevronRight />
                                </span>
                            </a>
                        </div>

                        <!-- site sections (except current) -->
                        <div class="py-3 space-y-4 text-sm">
                            <a
                                v-for="(section, sectionIndex) in visibleSiteSections"
                                :key="sectionIndex"
                                :href="section.url"
                                class="block hover:text-blue-light"
                            >
                                {{ section.text }}
                            </a>

                            <!-- Near Me -->
                            <button
                                v-for="(menu, menuIndex) in nearMe"
                                v-if="canShow(menu.audience)"
                                :key="menuIndex + visibleSiteSections.length"
                                :href="menu.url"
                                @click.stop="selectMenu($event, menu)"
                                class="flex w-full items-center hover:text-blue-light"
                            >
                                <span>
                                    {{ menu.text }}
                                </span>
                                <span
                                    v-if="menu.items"
                                    class="ml-auto"
                                >
                                    <IconChevronRight />
                                </span>
                            </button>

                            <!-- Choose Langauge -->
                            <button
                                v-for="(menu, menuIndex) in languageMenu"
                                v-if="canShow(menu.audience)"
                                :key="menu.item"
                                :href="menu.url"
                                @click.stop="selectMenu($event, menu)"
                                class="flex w-full items-center hover:text-blue-light"
                            >
                                <span>
                                    {{menu.text}}
                                </span>
                                <span
                                    v-if="menu.items"
                                    class="ml-auto"
                                >
                                    <IconChevronRight />
                                </span>
                            </button>

                            <!-- From appNavBar -->
                            <!-- <li v-if="showLanguageSwitcher"> -->
                            <!--     <select class="hover:border-none border-none text-sm py-2 pl-2 pr-4 bg-right cursor-pointer hover:text-salmon" @change="changeLanguage"> -->
                            <!--         <option value="/es.>/www." :selected="!onSpanishSite">EN</option> -->
                            <!--         <option value="/www.>/es." :selected="onSpanishSite">ES</option> -->
                            <!--     </select> -->
                            <!-- </li> -->

                            <!--mp_global_switch_begins-->
                            <!-- <a v-if="showLanguageSwitcher" -->
                            <!--    mporgnav -->
                            <!--    class="block user-locale" -->
                            <!--    @click="launchSwitcher()" title="Global Switch"> -->
                            <!--     Choose Language -->
                            <!-- </a> -->
                            <!--mp_global_switch_ends-->
                        </div>

                        <!-- legal links -->
                        <div class="py-4 text-sm">
                            <ul class="grid grid-cols-2 gap-y-3">
                                <li
                                    v-for="(link, index) in legalLinks"
                                    :key="index"
                                >
                                    <a
                                        :href="link.url"
                                        :target="target"
                                        :aria-label="link.text"
                                        class="hover:text-blue-light"
                                    >
                                        {{ link.text }}
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>
                </transition>

                <!-- submenu (dynamic) -->
                <transition name="slide-in-place">
                    <div
                        v-if="selectedMenu && !selectedSubmenu"
                        class="grid gap-8">
                        <!-- parent menu / go back -->
                        <button
                            @click.prevent="selectedMenu = null"
                            class="relative flex justify-between items-center px-4 py-3 z-40 w-full border-b border-gray-300"
                        >
                            <IconChevronLeft />
                            <div class="font-bold text-center text-xl p-2" v-html="selectedMenu.text">
                            </div>
                            <span><!-- empty --></span>
                        </button>

                        <a v-if="selectedMenu.url && selectedMenu.url.endsWith('/my-services')"
                            :href="currentSiteSection === 'business'? '/business/online-account-manager':'https://myaccount.breezeline.com/'"
                            class="btn-primary text-center current-section mx-4">
                                Sign in
                        </a>

                        <!-- menu items -->
                        <div
                            v-if="selectedMenu.items"
                            class="px-4">
                            <div
                                v-for="(item, itemIndex) in selectedMenu.items"
                                :key="itemIndex"
                                :class="dividerClass(item)">
                                <!-- Type: Group -->
                                <template v-if="item.type === 'group'">
                                    <div
                                        v-if="canShow(item.audience)"
                                        class="mb-12">
                                        <div
                                            v-for="(menuItem, menuItemIndex) in item.items"
                                            :key="menuItemIndex"
                                            :id="menuItem.text.replace(/\s/g, '')"
                                            :class="dividerClass(menuItem)">
                                            <!-- Has Submenu Items -->
                                            <div v-if="menuItem.items && menuItem.items.length > 0">
                                                <div @click.stop="selectSubmenu(menuItem)">
                                                    <div
                                                        v-if="menuItem.icon"
                                                        class="py-4 flex gap-2 items-center w-full justify-between">
                                                        <img :src="menuItem.icon" width="20" alt="">
                                                        <a
                                                            :href="menuItem.url"
                                                            :target="menuItem.target"
                                                            class="inline-block text-center text-navy-dark font-semibold">
                                                            {{ menuItem.text }}
                                                        </a>
                                                        <span v-if="menuItem.items" class="pl-6">
                                                            <IconChevronRight />
                                                        </span>
                                                    </div>
                                                    <div
                                                        v-else
                                                        class="py-4 w-full flex items-center justify-between">
                                                        <a
                                                            :href="menuItem.url"
                                                            :target="menuItem.target"
                                                            class="inline-block text-center text-navy-dark font-semibold">
                                                            {{ menuItem.text }}
                                                        </a>
                                                        <span v-if="menuItem.items" class="pl-6">
                                                            <IconChevronRight />
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- No Submenu Items, open href link -->
                                            <div v-else>
                                                <div
                                                    v-if="menuItem.icon"
                                                    class="py-4 flex gap-2 items-center">
                                                    <img :src="menuItem.icon" width="20" alt="" />
                                                    <a :href="menuItem.url"
                                                        :target="menuItem.target"
                                                        class="w-full flex justify-between items-center text-center text-navy-dark font-semibold">
                                                        {{ menuItem.text }}
                                                    </a>
                                                </div>
                                                <div v-else-if="menuItem && !menuItem.icon && menuItem.text">
                                                    <div class="flex items-center justify-between py-4">
                                                        <a :href="menuItem.url"
                                                            :target="menuItem.target"
                                                            class="w-full flex justify-between items-center text-center text-navy-dark font-semibold">
                                                            {{ menuItem.text }}
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <!-- Type: Link -->
                                <template v-else>
                                    <div
                                        v-if="canShow(item.audience)"
                                        :class="dividerClass(item)">
                                        <div v-if="item.items && item.items.length > 0">
                                            <!-- Submenu items exist -->
                                            <div @click.stop="selectSubmenu(item)">
                                                <!-- Has icon -->
                                                <div
                                                    v-if="item.icon"
                                                    class="py-4 flex gap-2 items-center">
                                                    <img
                                                        :src="item.icon"
                                                        width="20"
                                                        alt="" />
                                                    <a
                                                        :href="item.url"
                                                        :target="item.target"
                                                        class="w-full flex justify-between items-center text-center text-navy-dark font-semibold">
                                                        {{ item.text }}
                                                    </a>
                                                    <span v-if="item.items" class="pl-6">
                                                        <IconChevronRight />
                                                    </span>
                                                </div>

                                                <!-- Has divider -->
                                                <div v-else
                                                    class="py-4 flex justify-between items-center">
                                                    <div
                                                        class="w-full flex justify-between items-center text-center text-navy-dark font-semibold">
                                                        {{ item.text }}
                                                    </div>
                                                    <span v-if="item.items" class="pl-6">
                                                        <IconChevronRight />
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <!-- No submenu items, open href link -->
                                        <div v-else
                                            class="py-4">
                                            <!-- Has icon -->
                                            <div
                                                v-if="item.icon"
                                                class="flex gap-2 items-center">
                                                <img
                                                    :src="item.icon"
                                                    width="20"
                                                    alt="" />
                                                <a
                                                    :href="item.url"
                                                    :target="item.target"
                                                    class="w-full flex justify-between items-center text-center text-navy-dark font-semibold">
                                                    {{ item.text }}
                                                </a>
                                            </div>
                                            <!-- Has divider -->
                                            <a v-else-if="item.language"
                                                @click="changeLanguage(item.language)"
                                                class="w-full flex justify-between items-center text-navy-dark font-semibold">
                                                {{ item.text }}
                                            </a>
                                            <a v-else
                                                :href="item.url"
                                                :target="item.target"
                                                class="w-full flex justify-between items-center text-navy-dark font-semibold">
                                                {{ item.text }}
                                            </a>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </transition>

                <!-- tertiary menu (dynamic) -->
                <transition name="slide-in-place">
                    <div v-if="selectedMenu && selectedSubmenu">
                        <!-- parent menu / go back -->
                        <button
                            @click.prevent="selectedSubmenu = null"
                            class="relative flex justify-between items-center px-4 py-3 z-40 w-full border-b border-gray-300">
                            <IconChevronLeft />
                            <div class="font-bold text-center text-xl p-2">
                                {{ selectedSubmenu.text }}
                            </div>
                            <span><!-- empty --></span>
                        </button>

                        <!-- search -->
                        <div
                            class="px-4 mt-4 pb-24">
                            <div v-if="selectedMenu && selectedSubmenu.text === 'Support'">
                                <span class="font-bold text-xl">Search Support</span>
                                <form
                                    target="_blank"
                                    action="/search">
                                        <input
                                            type="search"
                                            name="search"
                                            id="search"
                                            placeholder="Search..."
                                            aria-label="Search field"
                                            class="w-full rounded border-0 mt-4 py-4 pl-8 text-lg text-black shadow">
                                </form>
                            </div>

                            <div v-for="(menuItem, menuItemIndex) in selectedSubmenu.items"
                                :key="menuItemIndex"
                                :class="dividerClass(menuItem)">
                                <!-- submenu items -->
                                <div v-if="menuItem.type === 'group'"
                                    class="mt-12">
                                   <h4 v-if="menuItem.text"
                                        class="text-lg mt-12">{{ menuItem.text }}</h4>
                                    <ul>
                                        <li v-for="(menuItemGroup, menuItemGroupIndex) in menuItem.items"
                                            :key="menuItemGroupIndex"
                                            :class="dividerClass(menuItemGroup)"
                                            class="py-4">
                                            <div
                                                v-if="menuItemGroup.icon"
                                                class="py-4 flex gap-2 items-center">
                                                <img
                                                    :src="menuItemGroup.icon"
                                                    width="20"
                                                    alt="" />
                                                <a
                                                    :href="menuItemGroup.url"
                                                    :target="menuItemGroup.target"
                                                    class="w-full flex justify-between items-center text-center text-navy-dark font-semibold">
                                                    {{ menuItemGroup.text }}
                                                </a>
                                                <span v-if="menuItemGroup.items">
                                                    <IconChevronRight />
                                                </span>
                                            </div>
                                            <a v-else
                                                :href="menuItemGroup.url"
                                                :target="menuItemGroup.target ? '_blank' : ''"
                                                class="text-navy-dark">
                                                {{ menuItemGroup.text }}
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                                <div v-else
                                    :class="dividerClass(menuItem)"
                                    class="py-4">
                                    <div
                                        v-if="menuItem.icon"
                                        class="py-4 flex gap-2 items-center">
                                        <img
                                            :src="menuItem.icon"
                                            width="20"
                                            alt="" />
                                        <a
                                            :href="menuItem.url"
                                            :target="menuItem.target"
                                            class="w-full flex justify-between items-center text-center text-navy-dark font-semibold">
                                            {{ menuItem.text }}
                                        </a>
                                        <span v-if="menuItem.items">
                                            <IconChevronRight />
                                        </span>
                                    </div>
                                    <a v-else
                                        :href="menuItem.url"
                                        :target="menuItem.target ? '_blank' : ''"
                                        class="text-navy-dark">
                                        {{ menuItem.text }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </transition>
            </div>
        </transition>
    </div>
</template>

<script>
    import { mapState} from 'vuex'
    import AppLogo from "../../atoms/AppLogo/appLogo.vue";
    import IconBars from "@/atoms/icons/IconBars"
    import IconChevronLeft from "@/atoms/icons/IconChevronLeft"
    import IconChevronRight from "@/atoms/icons/IconChevronRight"
    import IconPhone from "@/atoms/icons/IconPhone"
    import IconSearch from "@/atoms/icons/IconSearch"
    import IconXmark from "@/atoms/icons/IconXmark"

    export default {
        name: 'AppNavMenuMobile',
        components: {
            AppLogo,
            IconBars,
            IconChevronLeft,
            IconChevronRight,
            IconPhone,
            IconSearch,
            IconXmark,
        },
        props: {
            includeSearch: {
                type: Boolean,
                default: () => true,
            },
            logoLink: {
                type: String,
                required: true,
            },
            siteSections: {
                type: Array,
                required: true,
            },
            currentSiteSection: {
                type: String,
                required: true,
            },
            menu: {
                type: Array,
                required: true,
            },
            nearMe: {
                type: Array,
                required: false,
            },
            userTools: {
                type: Array,
                required: true,
            },
            phoneNumber: {
                type: String,
                required: false,
            },
            legalLinks: {
                type: Array,
                required: true,
            },
            target: {
                type: String,
                default: null,
            },
            canShow: {
                type: Function,
                required: true,
            },
            isCarrierPage: {
                type: Boolean,
            },
            carrierPageNumber: {
                type: String,
                required: false,
            }
        },
        data: () => ({
            languageMenu: [{
                text: 'Choose Language',
                items: [{text: 'EN', language: '/es.>/www.' }, {text: 'ES', language: '/www.>/es.' }],
                url: null
            }],
            searchTerms: '',
            d: false,
            sliderIsVisible: false,

            selectedMenu: null,
            selectedSubmenu: null,
        }),
        computed: {
            ...mapState(['meta']),
            showLanguageSwitcher() {
                return (this.currentSiteSection === 'residential')
            },
            siteSection () {
                return this.currentSiteSection[0].toUpperCase() + this.currentSiteSection.slice(1)
            },
            visibleSiteSections() {
                return this.siteSections.filter(s => s.text.toLowerCase() !== this.currentSiteSection)
            },
            allMenu () {
                return [
                    ...this.menu,
                    ...this.userTools,
                ]
            },
            logoLinkDestination () {
                if (this.logoLink === '/residential' || this.target === '_parent') {
                    return '/'
                } else {
                    return this.logoLink
                }
            },
            dividerClass() {
                return (item, menuItem, menuItemGroup) => {
                    if (item && !item.icon && item.text || menuItem && !menuItem.icon && menuItem.text || (menuItemGroup && !menuItemGroup.icon && menuItemGroup.text)) {
                        return 'border-b border-gray-200 last:border-b-0';
                    } else {
                        return '';
                    }
                }
            },
        },
        mounted() {
            if(document.getElementById('appNavSkeleton')){
                document.getElementById('appNavSkeleton').remove()
            }
        },
        methods: {
            updateUrl(path) {
                this.languageMenu.forEach(item => {
                    item.items.forEach(subItem => {
                        subItem.url = path;
                    });
                });
            },
            changeLanguage(value) {
                if (value){
                    const fromTo = value.split('>');
                    window.location.href =  window.location.href.replace(fromTo[0], fromTo[1]);
                }
            },
            mobileIconPhoneNum(){
                return this.meta.marketType === 'ohio' ? '844-489-7506'
                : this.meta.marketType === 'fiber-competitive-equal-speed' ? '855-284-3807'
                : this.meta.marketType === 'default' ? '855-706-7455'
                : '855-706-7455'
            },
            toggleSearch () {
                if (this.d) {
                    this.searchTerms = ''
                    this.d = false
                    this.$emit('overlay-closed')
                } else {
                    this.d = true
                    this.$emit('overlay-opened')
                }
            },
            showSlider () {
                this.sliderIsVisible = true
                this.$emit('overlay-opened')
            },
            hideSlider () {
                this.selectedMenu = null
                this.selectedSubmenu = null
                this.sliderIsVisible = false
                this.$emit('overlay-closed')
            },
            selectMenu(event, menu) {
                if (menu.items) {
                    this.selectedMenu = menu
                    event.preventDefault()
                }
            },
            selectSubmenu(item) {
                if (item.items && item.items.length > 0) {
                    // Handle submenu item selection
                    this.selectedSubmenu = item;
                } else {
                    // Handle regular menu item selection
                    console.log('Menu item selected:', item);
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    // for the search
    .slide-down-enter-active, .slide-down-leave-active {
        transition: all 250ms;
    }
    .slide-down-enter, .slide-down-leave-to {
        transform: translateY(-100%);
    }

    // for the menu
    .slide-in-place-enter-active, .slide-in-place-leave-active {
        transition: all 250ms;
    }
    .slide-in-place-enter, .slide-in-place-leave-to {
        opacity: 0;
        position: absolute;
        width: 100%;
        transform: translateX(100%);
    }
    input[type='search'] {
        background-position: right 1.5em top 50% !important;
        background-repeat: no-repeat;
        background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14.2939 12.5786H13.3905L13.0703 12.2699C14.191 10.9663 14.8656 9.27387 14.8656 7.43282C14.8656 3.32762 11.538 0 7.43282 0C3.32762 0 0 3.32762 0 7.43282C0 11.538 3.32762 14.8656 7.43282 14.8656C9.27387 14.8656 10.9663 14.191 12.2699 13.0703L12.5786 13.3905V14.2939L18.2962 20L20 18.2962L14.2939 12.5786V12.5786ZM7.43282 12.5786C4.58548 12.5786 2.28702 10.2802 2.28702 7.43282C2.28702 4.58548 4.58548 2.28702 7.43282 2.28702C10.2802 2.28702 12.5786 4.58548 12.5786 7.43282C12.5786 10.2802 10.2802 12.5786 7.43282 12.5786Z' fill='%23001E62'/%3E%3C/svg%3E%0A") !important;
    }
</style>
